import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Eye, 
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  UserX
} from 'lucide-react';
import SuperAdminSidebar from '../../../components/SuperAdminsidebar';
import Pagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { fetchUsers, activateDeactivateUser } from '../../../../services/superadmin_services/user_management_services';

const ManageUsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });
  const [error, setError] = useState('');
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch users on component mount and when filters change
  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.page_size]);

  const loadUsers = async (searchParams = {}) => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        search: searchParams.search || searchTerm,
        status: searchParams.status || statusFilter,
        page: searchParams.page || pagination.page,
        page_size: searchParams.page_size || pagination.page_size
      };

      const response = await fetchUsers(params);
      
      setUsers(response.data.results);
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages,
        count: response.data.count
      });
    } catch (error) {
      setError(error.message);
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    // Reset to first page when searching
    await loadUsers({ page: 1 });
  };

  const handleReset = async () => {
    setSearchTerm('');
    setStatusFilter('all');
    // Reset to first page and load all users
    await loadUsers({ search: '', status: 'all', page: 1 });
  };

  const handleToggleUserStatusClick = (user) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      const response = await activateDeactivateUser(selectedUser.id);
      
      // Update the user in the local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === selectedUser.id 
            ? { ...user, ...response.data.user }
            : user
        )
      );
      
      // Show success message (you can add a toast notification here)
      console.log(response.message);
    } catch (error) {
      setError(error.message);
      console.error('Error toggling user status:', error);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setSelectedUser(null);
    }
  };

  const handleCancelToggleStatus = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (page_size) => {
    setPagination(prev => ({ ...prev, page_size, page: 1 }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getUserRole = (user) => {
    if (user.is_superuser) return 'Super Admin';
    if (user.is_staff) return 'Admin';
    return 'User';
  };

  const getRoleBadgeColor = (user) => {
    if (user.is_superuser) return 'bg-secondary-100 text-secondary-800';
    if (user.is_staff) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getConfirmationMessage = (user) => {
    const isActive = user.active_status === 'active' || user.is_active;
    const action = isActive ? 'deactivate' : 'activate';
    const userName = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user.username;
    
    return `Are you sure you want to ${action} ${userName}? ${
      isActive 
        ? 'This will prevent the user from accessing the system.' 
        : 'This will allow the user to access the system again.'
    }`;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Manage Users</h1>
              <p className="text-gray-600 mt-2">View and manage all system users</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row md:items-end md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1 max-w-md">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  id="search"
                  type="text"
                  placeholder="Search by username, email, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-end space-x-4">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    id="status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-secondary-600 text-white px-6 py-2 rounded-lg hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={loading}
                  className="bg-tertiary-500 text-white px-4 py-2 rounded-lg hover:bg-tertiary-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading && users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No users found.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">User</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Role</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Status</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Email</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Phone</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Date Joined</th>
                      <th className="text-left py-3 px-6 font-medium text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-secondary-600">
                                {user.first_name?.[0] || user.username[0]}{user.last_name?.[0] || ''}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {user.first_name && user.last_name 
                                  ? `${user.first_name} ${user.last_name}` 
                                  : user.username
                                }
                              </p>
                              <p className="text-sm text-gray-600">@{user.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user)}`}>
                            {getUserRole(user)}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(user.active_status || (user.is_active ? 'active' : 'inactive'))}
                            <span className="text-sm font-medium capitalize">
                              {user.active_status || (user.is_active ? 'active' : 'inactive')}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-600">{user.email}</span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-600">{user.phone || 'N/A'}</span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-600">
                            {new Date(user.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/superadmin/users/${user.id}`}
                              className="p-2 text-gray-400 hover:text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => handleToggleUserStatusClick(user)}
                              disabled={loading}
                              className={`p-2 rounded-lg transition-colors ${
                                (user.active_status === 'active' || user.is_active)
                                  ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                              } disabled:opacity-50`}
                              title={(user.active_status === 'active' || user.is_active) ? 'Deactivate User' : 'Activate User'}
                            >
                              {(user.active_status === 'active' || user.is_active) ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                              title="More Actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                totalItems={pagination.count}
                pageSize={pagination.page_size}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={loading}
                itemName="users"
              />
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelToggleStatus}
        onConfirm={handleConfirmToggleStatus}
        title={selectedUser ? 
          `${(selectedUser.active_status === 'active' || selectedUser.is_active) ? 'Deactivate' : 'Activate'} User` 
          : 'Confirm Action'
        }
        message={selectedUser ? getConfirmationMessage(selectedUser) : ''}
      />
    </div>
  );
};

export default ManageUsersPage;